import {
    FILE_UPLOAD_CONTEXT,
    FILE_UPLOAD_REJECTION_REASON,
    MAX_REJECTIONS_PER_REQUEST,
} from '@/constants/fileUploadRejection.js';
import { storeFileUploadRejections } from '@/services/fileUploadRejectionService.js';

const MAX_EXTENSION_LENGTH = 32;
const MAX_PAGE_URL_LENGTH = 2048;

/**
 * Lowercased extension without the dot, or null when the name has none.
 *
 * @param {string|undefined|null} fileName
 * @returns {string|null}
 */
export function fileExtensionOf(fileName) {
    if (typeof fileName !== 'string') {
        return null;
    }

    const dotIndex = fileName.lastIndexOf('.');

    if (dotIndex <= 0 || dotIndex === fileName.length - 1) {
        return null;
    }

    return fileName
        .slice(dotIndex + 1)
        .toLowerCase()
        .slice(0, MAX_EXTENSION_LENGTH);
}

/**
 * The current page without its query string or hash, which can carry tokens.
 *
 * @returns {string}
 */
function currentPageUrl() {
    const { origin, pathname } = window.location;

    return `${origin}${pathname}`.slice(0, MAX_PAGE_URL_LENGTH);
}

function toBytes(value) {
    const bytes = Number(value);

    return Number.isFinite(bytes) && bytes >= 0 ? Math.round(bytes) : null;
}

function httpStatusOf(error) {
    const status = Number(error?.response?.status ?? error?.status);

    return Number.isInteger(status) && status >= 100 && status <= 599 ? status : null;
}

/** Request fields that carry files, from the upload endpoints' validation rules. Over-matching only keeps a row. */
const FILE_FIELD_PATTERN =
    /media|file|attachment|photo|image|video|logo|package|upload|csv|document|avatar|quiz|slide/i;

/**
 * A 422 whose errors name only non-file fields (a title, points, a date) did not
 * reject the files sent with it, so it is not an upload rejection.
 *
 * @param {unknown} error
 * @returns {boolean}
 */
function isValidationErrorUnrelatedToFiles(error) {
    if (httpStatusOf(error) !== 422) {
        return false;
    }

    const errors = error?.response?.data?.errors;
    const fields = errors && typeof errors === 'object' ? Object.keys(errors) : [];

    return fields.length > 0 && !fields.some(field => FILE_FIELD_PATTERN.test(field));
}

/**
 * Record rejected uploads without affecting the caller. Never throws and never
 * returns a promise, so upload code keeps its existing behavior whatever happens here.
 * The file name is never sent; only its extension and size.
 *
 * @param {string} context a FILE_UPLOAD_CONTEXT value
 * @param {Array<{file?: File|{name?: string, size?: number}|null, reason: string, maxFileSizeBytes?: number|null, httpStatus?: number|null}>} rejections
 * @returns {void}
 */
export function reportFileUploadRejections(context, rejections) {
    try {
        if (!Array.isArray(rejections) || rejections.length === 0) {
            return;
        }

        const pageUrl = currentPageUrl();
        const payload = rejections.map(({ file, reason, maxFileSizeBytes = null, httpStatus = null }) => ({
            context: context || FILE_UPLOAD_CONTEXT.UNSPECIFIED,
            page_url: pageUrl,
            file_extension: fileExtensionOf(file?.name),
            file_size_bytes: toBytes(file?.size),
            max_file_size_bytes: maxFileSizeBytes == null ? null : toBytes(maxFileSizeBytes),
            reason,
            http_status: httpStatus,
        }));

        for (let start = 0; start < payload.length; start += MAX_REJECTIONS_PER_REQUEST) {
            storeFileUploadRejections(payload.slice(start, start + MAX_REJECTIONS_PER_REQUEST)).catch(() => {});
        }
    } catch {
        // Logging is best effort and must never break an upload.
    }
}

/**
 * Record one rejected file.
 *
 * @param {string} context
 * @param {File|{name?: string, size?: number}|null} file
 * @param {string} reason a FILE_UPLOAD_REJECTION_REASON value
 * @param {{maxFileSizeBytes?: number|null, httpStatus?: number|null}} [details]
 * @returns {void}
 */
export function reportFileUploadRejection(context, file, reason, details = {}) {
    reportFileUploadRejections(context, [{ file, reason, ...details }]);
}

/**
 * Record a failed upload request as a server error, one row per file sent.
 * Pass no files when the request did not carry identifiable files. Cancelled
 * requests and 422s about non-file fields are not recorded.
 *
 * @param {string} context
 * @param {Array<File|{name?: string, size?: number}>|File|null} files
 * @param {unknown} error the rejected request's error
 * @returns {void}
 */
export function reportFileUploadServerError(context, files, error) {
    try {
        if (error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError' || error?.name === 'AbortError') {
            return;
        }

        if (isValidationErrorUnrelatedToFiles(error)) {
            return;
        }

        const list = (Array.isArray(files) ? files : [files]).filter(Boolean);
        const httpStatus = httpStatusOf(error);
        const reason = FILE_UPLOAD_REJECTION_REASON.SERVER_ERROR;

        reportFileUploadRejections(
            context,
            list.length > 0 ? list.map(file => ({ file, reason, httpStatus })) : [{ file: null, reason, httpStatus }]
        );
    } catch {
        // Logging is best effort and must never break an upload.
    }
}
